import { ButtonGroup, Flex, Input, Text } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PERIODS, parsePeriod, periodLabel, periodRange, type OverviewPeriod, type PeriodRange } from "./procurementFigures";
import { FilterChip } from "../../common/components/FilterChip";

export type PeriodSet<P extends string> = {
  periods: readonly P[];
  parse: (value: string | null) => P;
  label: (t: TFunction, period: P) => string;
  range: (period: P, today: Date, custom: { from: string | null; to: string | null }) => PeriodRange | null;
  unbounded?: P;
};

const LOOKING_BACK: PeriodSet<OverviewPeriod> = {
  periods: PERIODS,
  parse: parsePeriod,
  label: periodLabel,
  range: periodRange,
};

export type OverviewPeriodState<P extends string = OverviewPeriod> = {
  set: PeriodSet<P>;
  period: P;
  range: PeriodRange | null;
  isUnbounded: boolean;
  customFrom: string | null;
  customTo: string | null;
  choosePeriod: (next: P) => void;
  setCustomDate: (key: "from" | "to", value: string) => void;
};

export function useOverviewPeriod(): OverviewPeriodState;
export function useOverviewPeriod<P extends string>(set: PeriodSet<P>): OverviewPeriodState<P>;
export function useOverviewPeriod<P extends string>(
  set: PeriodSet<P> = LOOKING_BACK as unknown as PeriodSet<P>,
): OverviewPeriodState<P> {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = set.parse(searchParams.get("period"));
  const customFrom = searchParams.get("from");
  const customTo = searchParams.get("to");
  const range = useMemo(
    () => set.range(period, new Date(), { from: customFrom, to: customTo }),
    [set, period, customFrom, customTo],
  );

  const updateParams = (mutate: (params: URLSearchParams) => void) =>
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        mutate(params);
        return params;
      },
      { replace: true },
    );

  const choosePeriod = (next: P) =>
    updateParams((params) => {
      params.set("period", next);
      if (next !== "custom") {
        params.delete("from");
        params.delete("to");
      } else if (range && !params.get("from") && !params.get("to")) {
        params.set("from", range.from);
        params.set("to", range.to);
      }
    });

  const setCustomDate = (key: "from" | "to", value: string) =>
    updateParams((params) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });

  return {
    set,
    period,
    range,
    isUnbounded: period === set.unbounded,
    customFrom,
    customTo,
    choosePeriod,
    setCustomDate,
  };
}

export const OverviewPeriodChoice = <P extends string>({ state }: { state: OverviewPeriodState<P> }) => {
  const { t } = useTranslation();
  const { set, period, customFrom, customTo, choosePeriod, setCustomDate } = state;
  return (
    <>
      <ButtonGroup size="sm" spacing="0" gap="2" flexWrap="wrap">
        {set.periods.map((choice) => (
          <FilterChip
            key={choice}
            active={period === choice}
            label={set.label(t, choice)}
            onClick={() => choosePeriod(choice)}
          />
        ))}
      </ButtonGroup>
      {period === "custom" && (
        <Flex
          gap="2"
          direction={{ base: "column", md: "row" }}
          align={{ base: "flex-start", md: "center" }}
          data-testid="custom-period-range"
        >
          <Input
            type="date"
            size="sm"
            w="40"
            aria-label={String(t("From"))}
            value={customFrom ?? ""}
            onChange={(event) => setCustomDate("from", event.target.value)}
          />
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
          <Input
            type="date"
            size="sm"
            w="40"
            aria-label={String(t("To"))}
            value={customTo ?? ""}
            onChange={(event) => setCustomDate("to", event.target.value)}
          />
        </Flex>
      )}
    </>
  );
};

export const IncompletePeriod = () => {
  const { t } = useTranslation();
  return (
    <Text fontSize="sm" color="smMuted" data-testid="custom-period-incomplete">
      {t("Choose a first and a last day, with the first on or before the last.")}
    </Text>
  );
};
